import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  selectSearchAndFilter,
  setColumnFilters,
  setExtraFilters,
  setSortOrder,
} from 'app/store/filtering/searchAndFilterSlice';
import { HighnoteTransactionEvent } from 'API';
import {
  SEARCH_SORT_DIRECTION,
  TRANSACTION_SORT_FIELD,
  LocalTransactionFilterInput,
} from 'app/util/searchTypes';
import { selectTransactionsSlice } from 'app/pages/store/transactionsSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { getPageName } from 'util/helpers';
import { loadTransactionsDispatcher } from 'app/pages/overview/tabs/transactions/utils';

import type { ColumnFilterMap } from 'util/filtering/models';

interface UseTransactionsOptions {
  onlyMyTransactions?: boolean;
  extraFilters?: LocalTransactionFilterInput;
  columnFilters?: ColumnFilterMap;
}

interface UseTransactionsResult {
  loading: boolean;
  loadingMore: boolean;
  // SEE-1392: so a rejected fetch can render as a failure rather than as
  // "nothing needs your attention".
  hadErrorLoading: boolean;
  loadError: string | null;
  nextToken?: string;
  total: number;
  transactions: HighnoteTransactionEvent[];
  hasMoreTransactions: boolean;
  loadTransactions: (token?: string) => void;
  tableFilters: ReturnType<typeof selectSearchAndFilter>[string];
}

export const useTransactions = ({
  onlyMyTransactions = false,
  extraFilters = {},
  columnFilters = {},
}: UseTransactionsOptions = {}): UseTransactionsResult => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pageName = getPageName(location.pathname);

  const { selectedPaidolId, highnotePaymentCardIds } = useAppSelector(selectUserCompanies);
  const transactionsState = useAppSelector(selectTransactionsSlice);
  const { loading, loadingMore, nextToken, total, hadErrorLoading, loadError } = transactionsState;
  const transactions = transactionsState.ids
    .map((id) => transactionsState.entities[id])
    .filter((t): t is HighnoteTransactionEvent => t !== undefined);
  const tableFilters = useAppSelector(selectSearchAndFilter)[pageName];

  // Workaround for OpenSearch bug; last page of results always has
  // a nextToken value, even if there's nothing to display on the
  // next page.
  const hasMoreTransactions = total > transactions.length;

  const loadTransactions = useCallback(
    (token?: string) => {
      loadTransactionsDispatcher({
        dispatch,
        _nextToken: token,
        selectedPaidolId,
        pageName,
      });
    },
    [dispatch, selectedPaidolId, pageName]
  );

  // Set up initial filters
  useEffect(
    () => {
      if (selectedPaidolId && highnotePaymentCardIds.length > 0) {
        // Set sort order
        dispatch(
          setSortOrder({
            pageName,
            sortOption: {
              field: TRANSACTION_SORT_FIELD.eventDate,
              direction: SEARCH_SORT_DIRECTION.desc,
            },
          })
        );

        // Set column filters if provided
        if (Object.keys(columnFilters).length > 0) {
          dispatch(
            setColumnFilters({
              pageName,
              columnFilters,
            })
          );
        }

        // Set extra filters for personal transactions
        if (onlyMyTransactions) {
          dispatch(
            setExtraFilters({
              pageName,
              extraFilters: {
                or: highnotePaymentCardIds.map((id) => ({
                  paymentCardId: { eq: id },
                })),
              },
            })
          );
        }

        // Initial load
        loadTransactions();
      }
    },
    [
      /*     dispatch,
    selectedPaidolId,
    highnotePaymentCardIds,
    pageName,
    loadTransactions,
    onlyMyTransactions,
    extraFilters, */
    ]
  );

  return {
    loading,
    loadingMore,
    hadErrorLoading,
    loadError,
    nextToken,
    total,
    transactions,
    hasMoreTransactions,
    loadTransactions,
    tableFilters,
  };
};
